/**
 * userConsentsApi (1.34.0)
 *
 * Чтение и запись согласий текущего пользователя на юридические
 * документы Кворум.
 *
 * Доступ через RLS: каждый пользователь видит и пишет только свои
 * записи. UPDATE/DELETE не предусмотрены — согласия неизменяемы.
 */

import { isSupabaseConfigured, requireSupabase } from '../auth/supabaseClient';

export interface UserConsent {
  id: string;
  user_id: string;
  document_id: string;
  accepted_at: string;
  user_agent: string | null;
}

/**
 * Загружает все согласия указанного пользователя.
 * Возвращает Set из document_id для быстрой проверки «принят ли документ».
 */
export async function fetchUserConsentDocumentIds(
  userId: string,
): Promise<Set<string>> {
  if (!isSupabaseConfigured) return new Set();
  const supabase = requireSupabase();

  const { data, error } = await supabase
    .from('user_consents')
    .select('document_id')
    .eq('user_id', userId);

  if (error) throw new Error(`fetchUserConsentDocumentIds: ${error.message}`);
  const ids = new Set<string>();
  for (const row of (data ?? []) as Array<{ document_id: string }>) {
    ids.add(row.document_id);
  }
  return ids;
}

/**
 * Записывает согласие пользователя на конкретный документ.
 * Если такая запись уже есть (PK violation) — игнорируем.
 */
export async function recordUserConsent(
  userId: string,
  documentId: string,
  userAgent: string,
): Promise<void> {
  const supabase = requireSupabase();

  const { error } = await supabase
    .from('user_consents')
    .insert({
      user_id: userId,
      document_id: documentId,
      user_agent: userAgent,
    });

  // 23505 = unique violation, считаем что согласие уже было
  if (error && error.code !== '23505') {
    throw new Error(`recordUserConsent: ${error.message}`);
  }
}

/**
 * Записывает сразу несколько согласий — для случая когда пользователь
 * принимает все документы одним нажатием кнопки.
 */
export async function recordUserConsents(
  userId: string,
  documentIds: string[],
  userAgent: string,
): Promise<void> {
  if (documentIds.length === 0) return;
  const supabase = requireSupabase();

  const rows = documentIds.map((id) => ({
    user_id: userId,
    document_id: id,
    user_agent: userAgent,
  }));

  const { error } = await supabase
    .from('user_consents')
    .insert(rows);

  if (error && error.code !== '23505') {
    throw new Error(`recordUserConsents: ${error.message}`);
  }
}
